import React, { useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { isValidPhoneNumber } from "react-phone-number-input";

import { ReactComponent as RemoveIcon } from "../../../assets/cross.svg";
import { ReactComponent as GroupIcon } from "../../../assets/Group.svg";
import Checkbox from "../checkbox/checkbox";
import { useStyles, inline, inputDigitStyle } from "./verifyNumber.styles";
import {
  DigitKey,
  DIGIT_KEYS,
  VerifyNumberFormValues,
  VerifyNumberProps,
} from "./verifyNumber.types";

const VerifyNumber: React.FC<VerifyNumberProps> = ({
  phoneNumber = "000000000",
  onChangeNumber,
  handleClose,
  onVerify,
  onResend,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    control,
    formState: { isValid },
  } = useForm<VerifyNumberFormValues>({
    mode: "onChange",
    defaultValues: { a: "", b: "", c: "", d: "", e: "", f: "", smsConsent: false },
  });

  const inputRefs: Record<DigitKey, React.MutableRefObject<HTMLInputElement | null>> = {
    a: useRef<HTMLInputElement | null>(null),
    b: useRef<HTMLInputElement | null>(null),
    c: useRef<HTMLInputElement | null>(null),
    d: useRef<HTMLInputElement | null>(null),
    e: useRef<HTMLInputElement | null>(null),
    f: useRef<HTMLInputElement | null>(null),
  };

  const focusNext = (key: DigitKey) => {
    const nextKey = DIGIT_KEYS[DIGIT_KEYS.indexOf(key) + 1];
    inputRefs[nextKey]?.current?.focus();
  };

  // Backspace on an already-empty box clears the previous box and moves
  // focus back to it, so users can delete across the whole code (browsers
  // don't fire onChange for a no-op backspace on an empty input).
  const handleBackspace = (
    e: React.KeyboardEvent<HTMLInputElement>,
    key: DigitKey
  ) => {
    if (e.key !== "Backspace" || watch(key)) return;

    const prevKey = DIGIT_KEYS[DIGIT_KEYS.indexOf(key) - 1];
    if (!prevKey) return;

    e.preventDefault();
    setValue(prevKey, "", { shouldValidate: true });
    inputRefs[prevKey].current?.focus();
  };

  const registerDigit = (key: DigitKey) => {
    const { ref, onChange, ...rest } = register(key, {
      required: true,
      pattern: /^[0-9]$/,
    });

    return {
      ...rest,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(e);
        if (e.target.value) focusNext(key);
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) =>
        handleBackspace(e, key),
      ref: (el: HTMLInputElement | null) => {
        ref(el);
        inputRefs[key].current = el;
      },
    };
  };

  const onFormSubmit = (data: VerifyNumberFormValues) => {
    const { a, b, c, d, e, f } = data;
    onVerify({ a, b, c, d, e, f });
  };

  const canSubmit = isValid && isValidPhoneNumber(phoneNumber);

  return (
    <form className={classes.container} onSubmit={handleSubmit(onFormSubmit)}>
      <div className={classes.header} style={{ direction: t("css.side") }}>
        <span>{t("verifyNumber.verifyYourNumber")}</span>
        <span
          className="pointer"
          onClick={() => {
            handleClose();
          }}
        >
          <RemoveIcon style={{ height: 24, width: 24 }} />
        </span>
      </div>

      <div style={{ ...inline.h6Regular, marginBottom: 16 }}>
        {t("verifyNumber.textMessage")}
        <span> {phoneNumber} </span>
      </div>

      <div
        className="pointer"
        style={{
          ...inline.h6Medium,
          color: "#80BB34",
          marginBottom: 30,
          direction: t("css.side"),
        }}
        onClick={() => {
          onChangeNumber(true);
        }}
      >
        {t("verifyNumber.changeNumber")}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginBottom: 44,
          direction: t("css.side"),
        }}
      >
        <span style={{ ...inline.h7Regular, color: "#15222F", opacity: 0.4 }}>
          {t("verifyNumber.smsCode")}
        </span>
        {/* The code itself is numeric and always reads left-to-right, even
            in the Hebrew (rtl) layout — only the label above follows the
            page direction. */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            direction: "ltr",
          }}
        >
          {DIGIT_KEYS.map((key) => (
            <input
              key={key}
              maxLength={1}
              style={inputDigitStyle}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              {...registerDigit(key)}
            />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 24, direction: t("css.side") }}>
        <Controller
          name="smsConsent"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            <Checkbox
              value={t("verifyNumber.smsConsentLabel")}
              isChecked={value}
              disabled={value}
              facetValue={true}
              updateFacetsValues={() => onChange(true)}
              removeFacetsValues={() => onChange(false)}
            />
          )}
        />
        <div style={{ ...inline.h7Regular, color: "#15222F", opacity: 0.6 }}>
          <Trans
            i18nKey="verifyNumber.smsConsentText"
            components={{
              terms: (
                <Link
                  to="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#80BB34" }}
                />
              ),
              privacy: (
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: "#80BB34" }}
                />
              ),
            }}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          width: "100%",
          height: "56px",
          background: !canSubmit ? "lightGrey" : "#80BB34",
          color: "#ffffff",
          borderRadius: 16,
          border: "none",
          cursor: canSubmit ? "pointer" : "default",
          ...inline.h5Medium,
          marginBottom: 32,
        }}
      >
        {t("verifyNumber.verify")}
      </button>

      <div
        style={{
          textAlign: "center",
          ...inline.h6Medium,
          color: "#80BB34",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <GroupIcon style={{ marginRight: 8 }} />
        <span className="pointer" onClick={onResend}>
          {t("verifyNumber.resendCode")}
        </span>
      </div>
    </form>
  );
};

export default VerifyNumber;
